import { useState, useRef, useEffect, useCallback } from 'react';
import { safeLocalStorage } from '@/lib/storage-utils';
import { MessageCircle, Send, Reply, Trash2, X, Paperclip, Image as ImageIcon } from 'lucide-react';

export interface Comment {
  id: string;
  author: string;
  avatar?: string;
  text: string;
  timestamp: number;
  parentId?: string;
}

interface Props {
  /** Unique key for localStorage persistence, e.g. "page-<id>" or "section-<pageId>-<sectionId>" */
  threadId: string;
}

const STORAGE_PREFIX = 'sitemap-comments-';
const CURRENT_USER = 'You';

function getInitials(name: string) {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function CommentThread({ threadId }: Props) {
  const [comments, setComments] = useState<Comment[]>(() => {
    try {
      const stored = safeLocalStorage.getItem(STORAGE_PREFIX + threadId);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [newText, setNewText] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const currentThreadId = useRef(threadId);

  // Persist to localStorage and notify same-tab listeners
  // Only persist when the threadId matches (prevents overwriting during transitions)
  useEffect(() => {
    if (currentThreadId.current !== threadId) return;
    safeLocalStorage.setItem(STORAGE_PREFIX + threadId, JSON.stringify(comments));
    // Dispatch custom event so PageNodeCard comment badges update in real-time
    window.dispatchEvent(new CustomEvent('sitemap-comments-changed'));
  }, [comments, threadId]);

  // Re-load when threadId changes
  useEffect(() => {
    currentThreadId.current = threadId;
    try {
      const stored = safeLocalStorage.getItem(STORAGE_PREFIX + threadId);
      setComments(stored ? JSON.parse(stored) : []);
    } catch {
      setComments([]);
    }
    setReplyingTo(null);
    setNewText('');
  }, [threadId]);

  const addComment = useCallback((text: string, parentId?: string) => {
    if (!text.trim()) return;
    const comment: Comment = {
      id: crypto.randomUUID(),
      author: CURRENT_USER,
      text: text.trim(),
      timestamp: Date.now(),
      parentId,
    };
    setComments(prev => [...prev, comment]);
    setNewText('');
    setReplyingTo(null);
    // Scroll to bottom
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 50);
  }, []);

  const deleteComment = useCallback((id: string) => {
    // Delete comment and all its replies
    setComments(prev => prev.filter(c => c.id !== id && c.parentId !== id));
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent, parentId?: string) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (parentId) {
        addComment((e.target as HTMLTextAreaElement).value, parentId);
      } else {
        addComment(newText);
      }
    }
  };

  // Get top-level comments
  const topLevel = comments.filter(c => !c.parentId);
  const getReplies = (parentId: string) => comments.filter(c => c.parentId === parentId);

  const renderComment = (comment: Comment, isReply = false) => {
    const replies = getReplies(comment.id);
    const isOwn = comment.author === CURRENT_USER;

    return (
      <div key={comment.id} className={`${isReply ? 'ml-8 mt-2' : 'mt-3 first:mt-0'}`}>
        <div className="group relative">
          <div className="flex items-start gap-2.5">
            {/* Avatar */}
            <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold ${
              isOwn ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}>
              {getInitials(comment.author)}
            </div>

            <div className="flex-1 min-w-0">
              {/* Header */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-foreground">{comment.author}</span>
                <span className="text-[10px] text-muted-foreground">{timeAgo(comment.timestamp)}</span>
              </div>

              {/* Body */}
              <p className="text-sm text-foreground/90 mt-0.5 whitespace-pre-wrap break-words leading-relaxed">
                {comment.text}
              </p>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {!isReply && (
                  <button
                    onClick={() => { setReplyingTo(replyingTo === comment.id ? null : comment.id); setTimeout(() => replyInputRef.current?.focus(), 50); }}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Reply size={10} />
                    Reply
                  </button>
                )}
                {isOwn && (
                  <button
                    onClick={() => deleteComment(comment.id)}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 size={10} />
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Replies */}
        {replies.map(reply => renderComment(reply, true))}

        {/* Reply input */}
        {replyingTo === comment.id && (
          <div className="ml-8 mt-2 flex items-start gap-2">
            <div className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[9px] font-semibold mt-0.5">
              {getInitials(CURRENT_USER)}
            </div>
            <div className="flex-1 relative">
              <textarea
                ref={replyInputRef}
                placeholder="Write a reply..."
                className="w-full text-xs bg-muted border border-border rounded-lg px-3 py-2 pr-8 resize-none outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground min-h-[36px] max-h-[80px]"
                rows={1}
                onKeyDown={(e) => handleKeyDown(e, comment.id)}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = Math.min(target.scrollHeight, 80) + 'px';
                }}
              />
              <button
                onClick={() => {
                  const val = replyInputRef.current?.value;
                  if (val) addComment(val, comment.id);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
              >
                <Send size={12} />
              </button>
            </div>
            <button onClick={() => setReplyingTo(null)} className="mt-1 text-muted-foreground hover:text-foreground">
              <X size={12} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const composerJSX = (
    <div className="p-3 border-t border-border shrink-0">
      <div className="rounded-xl border border-border bg-muted/40 focus-within:border-primary/50 focus-within:bg-muted/60 transition-all">
        <textarea
          ref={inputRef}
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e)}
          placeholder="Write a comment..."
          className="w-full text-sm bg-transparent px-3.5 pt-3 pb-1 resize-none outline-none placeholder:text-muted-foreground min-h-[44px] max-h-[120px] text-foreground"
          rows={1}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            target.style.height = Math.min(target.scrollHeight, 120) + 'px';
          }}
        />
        <div className="flex items-center justify-between px-2 pb-2">
          <div className="flex items-center gap-0.5">
            <button
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Attach file"
            >
              <Paperclip size={14} />
            </button>
            <button
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Attach image"
            >
              <ImageIcon size={14} />
            </button>
          </div>
          <button
            onClick={() => addComment(newText)}
            disabled={!newText.trim()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Send size={12} />
            Send
          </button>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5 px-1">
        Press <kbd className="px-1 py-0.5 rounded bg-muted border border-border text-[9px] font-mono">Enter</kbd> to send · <kbd className="px-1 py-0.5 rounded bg-muted border border-border text-[9px] font-mono">Shift+Enter</kbd> for new line
      </p>
    </div>
  );

  if (topLevel.length === 0 && !newText) {
    return (
      <div className="flex-1 flex flex-col h-full">
        <div className="flex-1 flex flex-col items-center justify-center text-center px-5">
          <MessageCircle size={32} className="text-muted-foreground mb-3" />
          <p className="text-sm font-medium text-foreground mb-1">No comments yet</p>
          <p className="text-xs text-muted-foreground mb-4">Add a comment to start a discussion</p>
        </div>
        {composerJSX}
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 scrollbar-none">
        {topLevel.map(c => renderComment(c))}
      </div>
      {composerJSX}
    </div>
  );
}
